// .env.production is gitignored, so production builds fall back to the real API
// rather than silently calling relative paths on admin.thisplaceisyourspace.com.
export const API_BASE =
  import.meta.env.VITE_LUMINA_API_URL ||
  (import.meta.env.PROD ? 'https://api.thisplaceisyourspace.com' : '');

export class ApiError extends Error {
  status: number;
  /** Machine-readable reason from the API, e.g. `proxy_required`. */
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// Error bodies are `{ error, message?, code? }`; `message` carries the detail.
async function toApiError(res: Response): Promise<ApiError> {
  let message = res.statusText;
  let code: string | undefined;
  try {
    const data = (await res.json()) as { error?: string; message?: string; code?: string };
    message = [data.error, data.message].filter(Boolean).join(': ') || message;
    code = data.code;
  } catch {}
  return new ApiError(message, res.status, code);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function upload<T>(path: string, form: FormData): Promise<T> {
  // No Content-Type header: the browser sets multipart/form-data with the boundary.
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) throw await toApiError(res);
  return res.json() as Promise<T>;
}

// Sends a file as the raw request body so the API can stream it to R2 without
// parsing multipart — used for print masters, which can be very large.
async function uploadRaw<T>(path: string, file: File): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': file.type, 'X-Filename': encodeURIComponent(file.name) },
    body: file,
  });
  if (!res.ok) throw await toApiError(res);
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload,
  uploadRaw,
};
